/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { ChangeDetectionStrategy, Component, Input, OnDestroy } from '@angular/core';
import { GraphicalTakeOffPoint } from '@gh/builder-shared-data';
import { isNotNil } from '@gh/core-util';
import { compact, isNil } from 'lodash';
import { Item, Path, Point, Shape } from 'paper';
import { GraphicalTakeOffViewTransform } from '../../resources/models';
import {
  composePaperEffects,
  constantScalePaperEffect,
  constantScaleStrokeWidthPaperEffect,
  moveOnBack,
  toPaperPoint,
} from './canvas.utils';
import { GraphicalTakeOffElementBase, paperEffect, PaperElementEffect } from './graphical-take-off-element';

const setFirstPoint = (path: Item, item?: Item): void => {
  path['_draftPoint_'] = item;
};
const getFirstPoint = (path: Item): Maybe<Item> => path['_draftPoint_'];
const setFirstPointPosition = (path: Item, point: Point, scale: number) => {
  const firstPoint = getFirstPoint(path);
  if (firstPoint) {
    firstPoint.position = point;
    firstPoint['radius'] = 5 * scale;
    firstPoint['strokeWidth'] = scale;
  } else {
    const circle = Shape.Circle(point, 5 * scale);
    circle.strokeColor = path.strokeColor;
    circle.fillColor = 'white';
    circle['strokeWidth'] = scale;
    setFirstPoint(path, circle);
  }
};
const removeFirstPoint = (path: Item) => {
  const firstPoint = getFirstPoint(path);
  if (firstPoint) {
    firstPoint.remove();
    setFirstPoint(path);
  }
};

const scaleLinePaperEffects: PaperElementEffect[] = [
  paperEffect('viewTransform', composePaperEffects(constantScalePaperEffect, constantScaleStrokeWidthPaperEffect)),
  paperEffect(['point1', 'point2', 'viewTransform'], (path: Path,
                                                      [point1, point2, viewTransform],
                                                      [previousPoint1, previousPoint2, previousViewTransform]) => {
    if (point1 === previousPoint1
      && point2 === previousPoint2
      && viewTransform === previousViewTransform) {
      return;
    }

    path.removeSegments();

    if (isNotNil(point1) && isNil(point2)) {
      setFirstPointPosition(path, toPaperPoint(point1), viewTransform.scale);
    } else if (isNotNil(point1) && isNotNil(point2)) {
      removeFirstPoint(path);

      path.add(toPaperPoint(point1));
      path.add(toPaperPoint(point2));
    }
  }),
];

@Component({
  selector: 'gh-graphical-take-off-canvas-scale-line',
  template: '',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GraphicalTakeOffCanvasScaleLineComponent extends GraphicalTakeOffElementBase implements OnDestroy {
  @Input() point1: GraphicalTakeOffPoint;
  @Input() point2: GraphicalTakeOffPoint;
  @Input() viewTransform: GraphicalTakeOffViewTransform;

  constructor() {
    super(scaleLinePaperEffects);
  }

  ngOnDestroy(): void {
    removeFirstPoint(this.item);
    super.ngOnDestroy();
  }

  protected createPaperItem(): Item {
    const path = new Path(compact([this.point1, this.point2]).map(toPaperPoint));
    path.strokeColor = '#f00';
    path.strokeWidth = 3;

    moveOnBack(path, 'tool');

    return path;
  }
}
